import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuthContext } from '@/shared/context/useAuthContext';
import { agencyApi } from '@/modules/agency/api/agencyApi';
import { agencyKeys } from '@/modules/agency/queryKeys';
import { settingsApi } from '@/modules/settings/api/settingsApi';
import { locationsApi } from '@/modules/settings/api/locationsApi';
import { settingsKeys } from '@/modules/settings/queryKeys';
import styles from './AppLayout.module.css';

/**
 * The location a link asked for (`?location=<id>`, e.g. from a texting email), when the user has
 * it; else the primary one. Emails name the location so a multi-location practice lands on the
 * one the email is about.
 */
function initialLocation<T extends { id: string; isPrimary?: boolean }>(locs: T[]): T | undefined {
  const linked = new URLSearchParams(window.location.search).get('location');
  return locs.find((l) => l.id === linked) ?? locs.find((l) => l.isPrimary) ?? locs[0];
}

function ImpersonationBanner() {
  const { impersonatedPractice, stopImpersonation } = useImpersonation();
  const { setLocations, setCurrentLocation } = useLocation();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: agencyKeys.practice(impersonatedPractice?.id ?? ''),
    queryFn: () => agencyApi.getPractice(impersonatedPractice!.id),
    enabled: !!impersonatedPractice?.id,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const locations = data?.data?.locations;
    if (!locations) return;
    const locs = locations.map((l) => ({
      id: l.id,
      name: l.name,
      address: l.address,
      city: l.city,
      state: l.state,
      phone: l.phone,
      isPrimary: l.isDefault,
    }));
    setLocations(locs);
    const primary = initialLocation(locs);
    if (primary) setCurrentLocation(primary);
  }, [data]);

  if (!impersonatedPractice) return null;

  const exit = () => {
    stopImpersonation();
    setLocations([]);
    setCurrentLocation(null as never);
    navigate('/agency');
  };

  // TODO(agency-notifications/phase-3): add a "Practice Owner View" toggle
  // next to the banner. When enabled, every client module should hide
  // agency-only affordances (raw audit scores, draft campaign recs,
  // unapproved content, etc). Needs a context flag (`agencyViewMode`) that
  // each module reads and conditionally renders against — touches SEO / Ads /
  // Strategy / Settings individually, which is why it's deferred.
  // See PracticeNova_AgencyView_PRD_v2_Complete.md §11.1 for the toggle spec.
  return (
    <div className={styles.impersonationBanner}>
      <span className={styles.bannerLeft}>
        Viewing: <strong>{impersonatedPractice.name}</strong>
      </span>
      <button className={styles.commandCenterLink} onClick={exit}>
        ← Command Center
      </button>
    </div>
  );
}

export function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuthContext();
  const { impersonatedPractice, stopImpersonation } = useImpersonation();

  // Clear stale impersonation state if a non-agency user is logged in.
  // Agency admins persist impersonation in localStorage; a practice user logging
  // in the same browser would inherit it and trigger 403s on agency-only routes.
  useEffect(() => {
    if (user && !user.isAgency) {
      stopImpersonation();
    }
  }, [user?.id, user?.isAgency]);
  const queryClient = useQueryClient();
  const prevImpersonatedId = useRef<string | null | undefined>(undefined);
  const contentRef = useRef<HTMLElement>(null);
  const { pathname } = useRouterLocation();
  const { setLocations, setCurrentLocation } = useLocation();

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const currentId = impersonatedPractice?.id ?? null;
    // Skip the very first render (undefined → initial value)
    if (prevImpersonatedId.current === undefined) {
      prevImpersonatedId.current = currentId;
      return;
    }
    if (prevImpersonatedId.current !== currentId) {
      prevImpersonatedId.current = currentId;
      queryClient.removeQueries({ queryKey: ['settings'] });
    }
  }, [impersonatedPractice?.id]);

  const { data: practiceData } = useQuery({
    queryKey: settingsKeys.practice(),
    queryFn: () => settingsApi.getPractice(),
    enabled: !!(user?.practiceId || impersonatedPractice),
    staleTime: 5 * 60 * 1000,
  });

  // Load locations for practice users (agency users get locations via ImpersonationBanner)
  const { data: locationsData } = useQuery({
    queryKey: settingsKeys.locations(),
    queryFn: () => locationsApi.getAll(),
    enabled: !!user?.practiceId && !impersonatedPractice,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!locationsData?.data || impersonatedPractice) return;
    const locs = locationsData.data.map((l) => ({
      id: l.id,
      name: l.name,
      address: l.address,
      city: l.city,
      state: l.state,
      phone: l.phone,
      isPrimary: l.isDefault,
    }));
    setLocations(locs);
    const primary = initialLocation(locs);
    if (primary) setCurrentLocation(primary);
  }, [locationsData, impersonatedPractice]);

  const practiceName = impersonatedPractice?.name ?? practiceData?.data?.name;

  return (
    <div className={styles.layout}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className={styles.main}>
        <ImpersonationBanner />
        <TopBar practiceName={practiceName} />
        <main ref={contentRef} className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
